import React, {useCallback, useContext, useState, useRef} from 'react';
import {Button, SegmentedGroup, Segmented, setOptions, toast} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import '../styles/dashboardWorkspaceGraph.css';
import {Doughnut, Bar, Line, PolarArea, Radar, Pie, Bubble, Scatter} from "react-chartjs-2";
import Empty from "../utils/Empty";
import Print from "../utils/Print";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChartSimple, faCopy, faXmark} from "@fortawesome/pro-duotone-svg-icons";
import {NetworkContext} from "../utils/NetworkContext";

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function DashboardWorkspaceGraph({locale, workspaceItem, setLoading, teamtalk, navigation, ButtonBack, width}) {
    const {wsCall} = useContext(NetworkContext);
    const [menu, setMenu] = useState('original');
    const [iconHover, setIconHover] = useState(null);
    const chartRef = useRef(null);

    const GraphClose = useCallback(() => {
        ButtonBack();
    }, [ButtonBack]);

    const ChartImage = () => {
        if (!chartRef.current) return '';
        const base64 = chartRef.current.toBase64Image();
        return `<img src="${base64}" alt="${workspaceItem.name}" style="width:100%;max-width:700px;" />`;
    };

    const ChartCopy = async () => {
        try {
            if (!chartRef.current) throw new Error('Chart not ready');

            const canvas = chartRef.current.canvas;

            const blob = await new Promise((resolve, reject) =>
                canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/png')
            );

            await navigator.clipboard.write([
                new ClipboardItem({'image/png': blob})
            ]);

            toast({message: locale.dashboardWorkspaceGraph.e, color: 'info', display: 'bottom', duration: 1000});
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            toast({message: locale.dashboardWorkspaceGraph.f, color: 'danger', display: 'bottom', duration: 3000});
        }
    }

    const ButtonUpdate = data => {
        setLoading(true);

        const message = {
            type: 'wss',
            path: 'workspace-item',
            action: 'update',
            data: {
                id: workspaceItem.id,
                teamtalk: (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null,
                tab: (!Empty(teamtalk) && teamtalk.id) ? null : navigation.tab
            }
        };
        if (data === 'accept') {
            message.data.accept = true;
        }
        if (data === 'reject') {
            message.data.reject = true;
        }
        wsCall(message);
    }

    const SegmentedUpdate = data => {
        setMenu(data.target.value);
    }

    return (
        <div className="cs-dashboard-workspace-graph-fullscreen">
            <div className="mbsc-row cs-dashboard-workspace-graph-header" >
                <div className="mbsc-col cs-dashboard-workspace-graph-col-start" >
                    <Button color="secondary" variant="flat"  className="mbsc-bold cs-dashboard-workspace-graph-color" >
                        <FontAwesomeIcon className="cs-dashboard-workspace-graph-icon" icon={faChartSimple} />
                        <div className="cs-dashboard-workspace-graph-menu-text">
                            {workspaceItem.name}
                        </div>
                    </Button>
                </div>
                <div className="mbsc-col-auto" >
                    <div className="mbsc-row cs-dashboard-workspace-graph-col-end">
                        <Button onMouseEnter={() => setIconHover('copy')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-graph-color" onClick={() => ChartCopy()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-graph-icon" icon={faCopy} fade={iconHover === 'copy'} /><div className="cs-dashboard-workspace-graph-menu-text">{locale.dashboardWorkspaceGraph.g}</div>
                        </Button>
                        <Print
                            width={width}
                            locale={locale}
                            name={workspaceItem.name}
                            GetContent={ChartImage}
                        >
                            <img
                                src={chartRef.current?.toBase64Image()}
                                alt={workspaceItem.name}
                                style={{width: '100%', maxWidth: '700px'}}
                            />
                        </Print>
                        <Button onMouseEnter={() => setIconHover('close')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-code-color" onClick={() => GraphClose()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-graph-icon" icon={faXmark} fade={iconHover === 'close'} />
                        </Button>
                    </div>
                </div>
            </div>
            {workspaceItem.update &&
                <div className="mbsc-row cs-dashboard-workspace-graph-header-secondary" >
                    <div className="mbsc-col" />
                    <div className="mbsc-col-auto" >
                        <div className="mbsc-row cs-dashboard-workspace-graph-col-end">
                            <Button color="primary" variant="flat" className="mbsc-bold mbsc-txt-muted cs-dashboard-workspace-graph-color" onClick={() => ButtonUpdate('accept')} >
                                {locale.dashboardWorkspaceGraph.c}
                            </Button>
                            <Button color="primary" variant="flat" className="mbsc-bold mbsc-txt-muted cs-dashboard-workspace-graph-color" onClick={() => ButtonUpdate('reject')} >
                                {locale.dashboardWorkspaceGraph.d}
                            </Button>
                        </div>
                    </div>
                </div>
            }
            {workspaceItem.update &&
                <div className="mbsc-row mbsc-justify-content-center cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12 mbsc-col-sm-12 mbsc-col-md-10 mbsc-col-lg-8 mbsc-col-xl-6">
                        <SegmentedGroup className="cs-dashboard-workspace-graph-update" name="update" color="primary" value={menu} onChange={SegmentedUpdate}>
                            <Segmented value="original" >
                                {locale.dashboardWorkspaceGraph.a}
                            </Segmented>
                            <Segmented value="update" >
                                {locale.dashboardWorkspaceGraph.b}
                            </Segmented>
                        </SegmentedGroup>
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'doughnut') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'doughnut') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'doughnut')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12 mbsc-col-sm-10 mbsc-col-md-8 mbsc-col-lg-6 mbsc-col-xl-4">
                        <Doughnut ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'pie') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'pie') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'pie')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12 mbsc-col-sm-10 mbsc-col-md-8 mbsc-col-lg-6 mbsc-col-xl-4">
                        <Pie ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'polar') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'polar') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'polar')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12 mbsc-col-sm-10 mbsc-col-md-8 mbsc-col-lg-6 mbsc-col-xl-4">
                        <PolarArea ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'radar') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'radar') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'radar')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12 mbsc-col-sm-12 mbsc-col-md-10 mbsc-col-lg-8 mbsc-col-xl-6">
                        <Radar ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'bar') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'bar') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'bar')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12">
                        <Bar ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'line') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'line') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'line')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12">
                        <Line ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'bubble') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'bubble') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'bubble')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12">
                        <Bubble ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
            {((!workspaceItem.update && workspaceItem.data.type === 'scatter') || (workspaceItem.update && menu === 'original' && workspaceItem.data.type === 'scatter') || (workspaceItem.update && menu === 'update' && workspaceItem.data.update.type === 'scatter')) &&
                <div className="mbsc-row mbsc-justify-content-center mbsc-padding cs-dashboard-workspace-graph-charts">
                    <div className="mbsc-col-12">
                        <Scatter ref={chartRef} options={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.options : workspaceItem.data.update.options} data={(!workspaceItem.update || (workspaceItem.update && menu === 'original')) ? workspaceItem.data.data : workspaceItem.data.update.data} />
                    </div>
                </div>
            }
        </div>
    );
}

export default DashboardWorkspaceGraph;
